import type { ColumnDefinition } from 'components/SubTable';
import { SubTable } from 'components/SubTable';
import React, { useMemo } from 'react';

export type InvoiceItem = {
  id: string;
  description: string;
  productId: string;
  payoutParty: string;
  platform: boolean;
  amount: number | string;
  isStrikethrough?: boolean;
  isDisputed?: boolean;
};

type Props = {
  items: InvoiceItem[];
  platformDisplayName: string;
  onItemClick?: (item: InvoiceItem) => void;
};

const formatCurrency = (amount: number) =>
  `$${(amount / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const ItemsPane = ({
  items,
  platformDisplayName,
  onItemClick,
}: Props): JSX.Element => {
  // Define schema for items table
  const itemsSchema: readonly ColumnDefinition<InvoiceItem>[] = useMemo(
    () => [
      {
        title: 'Description',
        format: (item: InvoiceItem) => item.description,
      },
      {
        title: 'Seller',
        format: (item: InvoiceItem) => {
          const sellerLabel = item.platform
            ? platformDisplayName
            : item.payoutParty;
          return <span className="text-main-500">{sellerLabel}</span>;
        },
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        format: (item: InvoiceItem) => {
          const amount = formatCurrency(Number(item.amount));

          if (item.isStrikethrough) {
            return (
              <span className="line-through text-orange-500">{amount}</span>
            );
          }
          return amount;
        },
      },
    ],
    [platformDisplayName]
  );

  const handleItemClick = (item: InvoiceItem) => {
    if (onItemClick) {
      onItemClick(item);
    }
  };

  return (
    <SubTable
      data={items}
      schema={itemsSchema}
      onClick={handleItemClick}
      textSize="md"
      showHeader
      stickyHeader={false}
      emptyValue={{
        Description: <span className="text-main-500">No items</span>,
        Seller: '',
        Amount: '',
      }}
    />
  );
};
